/* ============================================================
   fleetOverviewView — PF-4 (TASK-74482edf): pure view models for
   the provider fleet surface. Two concerns:
   1. materialsChips — quick-assign material chips on the add/edit
      printer form: technology-aware presets map onto the GLOBAL
      material catalog (spec §5.2: no third source of truth — the
      catalog + per-printer links stay canonical), selection is a
      plain string set, and the save plan is a sequential list of
      AddProviderMaterial payloads (ProviderService AddMaterial-
      ToPrinterAsync calls, one per chip).
   2. fleetOverview — render-ready lines for the provider overview
      card: fleet summary (linked vs custom, catalog coverage) and
      the «Материалы: …» distinct union line.
   Pure functions only — no React, no fetch. Node-testable.
   ============================================================ */

import type { Material, MaterialType } from '../types';
import type { ProviderFleetOverview } from '../types/fleet';
import type { PrinterTechnology } from '../types/provider';
import { normalizePrinterTechnology } from './printerCatalogPickerView.ts';

/* ── Material chips (add/edit printer form) ─────────────── */

export interface MaterialChipView {
  materialId: string;
  name: string;
  type: MaterialType;
  selected: boolean;
}

/** Technology-aware quick-assign presets (UI hint only — resolves against the
 *  active material catalog, so the catalog stays the single source of truth). */
const PRESET_TYPES: Record<PrinterTechnology, MaterialType[]> = {
  Fdm: ['Pla', 'Petg'],
  Sla: ['ResinStandard'],
  Sls: ['Nylon'],
};

export function presetTypesForTechnology(technology: PrinterTechnology | number): MaterialType[] {
  // bb2f58e8: API enum wire format is ordinal numbers — normalize before the
  // lookup so the preset is never undefined (preset.includes crashed the
  // add-printer form); unknown values degrade to an empty preset.
  return PRESET_TYPES[normalizePrinterTechnology(technology)] ?? [];
}

/** Active catalog materials as chips; `selected` mirrors the current pick set. */
export function materialChips(materials: Material[], selected: MaterialIdSet): MaterialChipView[] {
  return materials
    .filter((m) => m.isActive)
    .map((m) => ({ materialId: m.id, name: m.name, type: m.type, selected: selected.has(m.id) }));
}

/** Plain immutable toggle over the pick set. */
export function toggleMaterial(selected: MaterialIdSet, materialId: string): MaterialIdSet {
  const next = new Set(selected);
  if (next.has(materialId)) next.delete(materialId);
  else next.add(materialId);
  return next;
}

/** Quick-assign: replace the pick set with the preset rows that exist in the
 *  active catalog (missing types are skipped, never fabricated). */
export function applyPreset(materials: Material[], technology: PrinterTechnology | number): MaterialIdSet {
  const preset = new Set<string>(presetTypesForTechnology(technology));
  return new Set(
    materials.filter((m) => m.isActive && preset.has(m.type)).map((m) => m.id),
  );
}

/** Sequential save plan — one AddProviderMaterial payload per picked chip
 *  (spec §5.2: sequential AddMaterialToPrinterAsync calls). */
export interface MaterialAssignment {
  materialId: string;
}

export function assignmentPlan(selected: MaterialIdSet): MaterialAssignment[] {
  return [...selected].map((materialId) => ({ materialId }));
}

/** Unassigned = active catalog materials the printer does not carry yet. */
export function unassignedMaterials(materials: Material[], linkedMaterialIds: Set<string>): Material[] {
  return materials.filter((m) => m.isActive && !linkedMaterialIds.has(m.id));
}

export type MaterialIdSet = Set<string>;

/* ── Fleet overview card ────────────────────────────────── */

export interface FleetOverviewView {
  fleetLine: string;
  materialsLine: string | null;
  coveragePercent: string;
  hasPrinters: boolean;
}

/** «Принтеры: 4 · активны 3 · из каталога 2 · свои 2» + «Материалы: …». */
export function fleetOverviewView(o: ProviderFleetOverview): FleetOverviewView {
  const fleetLine =
    `Принтеры: ${o.totalPrinters} · активны ${o.activePrinters}` +
    ` · из каталога ${o.catalogLinkedPrinters} · свои ${o.customPrinters}`;
  const names = o.materials.map((m) => m.materialName);
  return {
    fleetLine,
    materialsLine: names.length > 0 ? `Материалы: ${names.join(', ')}` : null,
    coveragePercent: `${Number(o.catalogCoveragePercent.toFixed(1))}%`,
    hasPrinters: o.totalPrinters > 0,
  };
}
